import { type CognitoUser } from "amazon-cognito-identity-js";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import type { AnchorBySlugResponse } from "@tilt/pos-sdk";

import { BrandFavicon } from "../components/BrandFavicon";
import { BrandStyles } from "../components/BrandStyles";
import { LanguagePicker } from "../components/LanguagePicker";
import { PoweredBy } from "../components/PoweredBy";
import { getPosClient } from "../lib/api";
import { readAnchorHint } from "../lib/anchor";
import { isPreProd } from "../lib/env";
import { usePageTitle } from "../lib/use-page-title";
import {
  completeNewPassword,
  completeTotp,
  confirmForgotPassword,
  forgotPassword,
  makeUser,
  makeUserPool,
  readAccessToken,
  signIn,
  verifyTotpSetup,
} from "../lib/auth";

/**
 * Sign-in flow on a per-anchor URL:
 *
 *   1. Page mounts at https://<slug>.pos.${env}.apps.myfinterra.com
 *      OR at the un-anchored ``pos.${env}…`` with a fallback query
 *      param (``?slug=…`` or ``?anchor=kind:uuid``).
 *   2. JS calls GET /pos/v1/public/anchor-by-slug → renders brand +
 *      "Sign in to <Anchor name>" before any auth happens.
 *   3. User types email + password.
 *   4. JS calls POST /pos/v1/public/sign-in-init with
 *      {email, anchor_kind, anchor_id} → returns the UUID Username.
 *   5. JS runs Cognito SRP with Username + password → tokens land in
 *      sessionStorage and we navigate to ``from`` (or "/").
 *
 * Three challenge branches Cognito can return mid-flow:
 *   * TOTP_REQUIRED       - show 6-digit prompt
 *   * NEW_PASSWORD_REQUIRED - first sign-in after Cognito invite
 */

type Stage =
  | { kind: "credentials" }
  | { kind: "totp"; user: CognitoUser }
  | { kind: "new_password"; user: CognitoUser }
  | { kind: "mfa_setup"; user: CognitoUser; secretCode: string; qrUri: string }
  | { kind: "forgot_email" }
  | { kind: "forgot_code"; user: CognitoUser; emailHint: string };

type AnchorState =
  | { kind: "loading" }
  | { kind: "ready"; anchor: AnchorBySlugResponse }
  | { kind: "missing" }
  | { kind: "error"; message: string };

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  usePageTitle("login");
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";

  const pool = useMemo(
    () =>
      makeUserPool({
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
        clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      }),
    [],
  );

  const [anchorState, setAnchorState] = useState<AnchorState>({ kind: "loading" });
  const [stage, setStage] = useState<Stage>({ kind: "credentials" });
  const savedEmail = localStorage.getItem("pos_remembered_email") ?? "";
  const [email, setEmail] = useState(savedEmail);
  const [rememberMe, setRememberMe] = useState(savedEmail !== "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Compute the merged brand once per anchor change. Hooks must come
  // before any early-return branch (rules-of-hooks); we read the
  // anchor lazily so the deps work for any anchorState kind.
  const merged = useMemo<Record<string, unknown>>(() => {
    if (anchorState.kind !== "ready") return {};
    const a = anchorState.anchor;
    return { ...a.partner_brand_config, ...a.brand_config };
  }, [anchorState]);

  const loadAnchor = useCallback((): (() => void) => {
    let cancelled = false;
    const hint = readAnchorHint();

    if (hint.direct) {
      // ``?anchor=kind:uuid`` was provided - skip the slug lookup
      // and synthesize a thin anchor object (no brand, no name -
      // the dogfood escape hatch).
      setAnchorState({
        kind: "ready",
        anchor: {
          anchor_kind: hint.direct.anchor_kind,
          anchor_id: hint.direct.anchor_id,
          name: null,
          brand_config: {},
          partner_brand_config: {},
        },
      });
      return () => undefined;
    }

    if (!hint.slug) {
      setAnchorState({ kind: "missing" });
      return () => undefined;
    }

    setAnchorState({ kind: "loading" });
    void getPosClient()
      .anchorBySlug(hint.slug)
      .then((anchor) => {
        if (!cancelled) setAnchorState({ kind: "ready", anchor });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setAnchorState({
            kind: "error",
            message: err instanceof Error ? err.message : "anchor lookup failed",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadAnchor(), [loadAnchor]);

  const anchorRetryCountRef = useRef(0);
  useEffect(() => {
    if (anchorState.kind !== "error") {
      anchorRetryCountRef.current = 0;
      return;
    }
    if (anchorRetryCountRef.current >= 3) return;
    const delay = Math.pow(2, anchorRetryCountRef.current) * 1000;
    anchorRetryCountRef.current += 1;
    const timer = setTimeout(() => {
      loadAnchor();
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [anchorState.kind, loadAnchor]);

  if (readAccessToken()) {
    return <Navigate to={from} replace />;
  }

  async function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    if (anchorState.kind !== "ready") return;
    if (rememberMe) {
      localStorage.setItem("pos_remembered_email", email.trim());
    } else {
      localStorage.removeItem("pos_remembered_email");
    }
    setError(null);
    setPending(true);
    try {
      const initResp = await getPosClient().signInInit({
        email: email.trim(),
        anchor_kind: anchorState.anchor.anchor_kind,
        anchor_id: anchorState.anchor.anchor_id,
      });
      const result = await signIn(pool, initResp.username, password);
      if ("kind" in result && result.kind === "TOTP_REQUIRED") {
        setStage({ kind: "totp", user: result.user });
      } else if ("kind" in result && result.kind === "NEW_PASSWORD_REQUIRED") {
        setStage({ kind: "new_password", user: result.user });
      } else if ("kind" in result && result.kind === "MFA_SETUP") {
        setStage({
          kind: "mfa_setup",
          user: result.user,
          secretCode: result.secretCode,
          qrUri: result.qrUri,
        });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(humaniseLoginError(err));
    } finally {
      setPending(false);
    }
  }

  async function handleTotp(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== "totp") return;
    setError(null);
    setPending(true);
    try {
      await completeTotp(stage.user, code.trim());
      navigate(from, { replace: true });
    } catch (err) {
      setError(humaniseLoginError(err));
    } finally {
      setPending(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== "new_password") return;
    setError(null);
    setPending(true);
    try {
      const result = await completeNewPassword(stage.user, newPassword, email.trim());
      if ("kind" in result && result.kind === "MFA_SETUP") {
        setStage({
          kind: "mfa_setup",
          user: result.user,
          secretCode: result.secretCode,
          qrUri: result.qrUri,
        });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(humaniseLoginError(err));
    } finally {
      setPending(false);
    }
  }

  async function handleMfaSetup(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== "mfa_setup") return;
    setError(null);
    setPending(true);
    try {
      await verifyTotpSetup(stage.user, code.trim());
      navigate(from, { replace: true });
    } catch (err) {
      setError(humaniseLoginError(err));
    } finally {
      setPending(false);
    }
  }

  // ----- Forgot password flow -----
  // POS Cognito uses UUIDs as Username so step 1 must resolve the
  // typed email to a username via /pos/v1/public/sign-in-init before
  // calling forgotPassword(). Step 2 collects the emailed code +
  // new password and calls confirmPassword.
  async function handleForgotEmail(e: React.FormEvent) {
    e.preventDefault();
    if (anchorState.kind !== "ready") return;
    setError(null);
    setPending(true);
    try {
      const initResp = await getPosClient().signInInit({
        email: email.trim(),
        anchor_kind: anchorState.anchor.anchor_kind,
        anchor_id: anchorState.anchor.anchor_id,
      });
      const user = makeUser(pool, initResp.username);
      await forgotPassword(user);
      setStage({ kind: "forgot_code", user, emailHint: email.trim() });
      setCode("");
      setNewPassword("");
    } catch (err) {
      setError(humaniseLoginError(err));
    } finally {
      setPending(false);
    }
  }

  async function handleForgotCode(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== "forgot_code") return;
    setError(null);
    setPending(true);
    try {
      await confirmForgotPassword(stage.user, code.trim(), newPassword);
      // Auto sign-in with the freshly-set password so the cashier
      // doesn't have to re-enter it.
      const result = await signIn(pool, stage.user.getUsername(), newPassword);
      if ("kind" in result && result.kind === "TOTP_REQUIRED") {
        setStage({ kind: "totp", user: result.user });
      } else if ("kind" in result && result.kind === "NEW_PASSWORD_REQUIRED") {
        // Shouldn't happen - password was just set - but be defensive.
        setStage({ kind: "new_password", user: result.user });
      } else if ("kind" in result && result.kind === "MFA_SETUP") {
        setStage({
          kind: "mfa_setup",
          user: result.user,
          secretCode: result.secretCode,
          qrUri: result.qrUri,
        });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(humaniseLoginError(err));
    } finally {
      setPending(false);
    }
  }

  // ----- Render branches ----- //

  if (anchorState.kind === "loading") {
    return <FullScreenSpinner label={t("common.loading")} />;
  }
  if (anchorState.kind === "missing") {
    // Pre-prod: show the URL convention so an operator can self-serve
    // (find the right anchored URL pattern). Prod: keep it generic
    // so we don't leak slug conventions to whoever lands on the bare
    // host.
    const bodyKey = isPreProd() ? "anchor.missing.bodyDetailed" : "anchor.missing.body";
    return (
      <FullScreenMessage tone="warn">
        <h1 className="text-lg font-semibold">{t("anchor.missing.title")}</h1>
        <p className="mt-2 text-sm" dangerouslySetInnerHTML={{ __html: t(bodyKey) }} />
      </FullScreenMessage>
    );
  }
  if (anchorState.kind === "error") {
    return (
      <FullScreenMessage tone="error">
        <h1 className="text-lg font-semibold">{t("anchor.error.title")}</h1>
        <p className="mt-2 text-sm">{t("anchor.error.body")}</p>
        <button
          type="button"
          className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          onClick={() => {
            loadAnchor();
          }}
        >
          {t("anchor.error.retry")}
        </button>
      </FullScreenMessage>
    );
  }

  const anchor = anchorState.anchor;
  const productName = pickStr(merged, "product_name") ?? t("login.productFallback");
  const tagline = pickStr(merged, "tagline");
  const loginBackgroundUrl = pickHttpsUrl(merged, "login_background_url");
  // Brand logo from the same merged (partner → org → location) cascade
  // every other surface uses. Same https-only allow-list as the
  // background image so a malicious brand_config can't ship a
  // ``javascript:`` payload.
  const logoUrl = pickHttpsUrl(merged, "logo_url");
  const anchorLabel = anchor.name ?? null;

  // The login background is rendered as a CSS background-image on the
  // outer container. Only ``https://`` URLs pass through (same allow-
  // list applied to font_import_url) so a malicious brand_config can't
  // ship a ``javascript:``/``data:`` payload.
  const outerStyle: React.CSSProperties | undefined = loginBackgroundUrl
    ? {
        backgroundImage: `url("${loginBackgroundUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  return (
    <>
      <BrandStyles brand={merged} />
      <BrandFavicon brand={merged} fallbackTitle={anchorLabel ?? productName} />
      <div
        className="flex min-h-screen flex-col items-center justify-center bg-surface-strong px-4 py-10"
        style={outerStyle}
      >
        <div className="w-full max-w-sm rounded-xl border border-line bg-surface p-6 shadow-sm">
          {logoUrl && (
            <div className="mb-4 flex justify-center">
              <img
                src={logoUrl}
                alt={anchorLabel ?? productName}
                // 56px keeps the logo prominent without dwarfing the
                // form. ``object-contain`` so non-square logos don't
                // distort. ``aria-hidden`` would be wrong (the alt
                // names the brand for SR users), but rendering a
                // duplicate of the productName text below would.
                // Capping height instead of fixing it lets unusually
                // wide logos breathe.
                className="max-h-14 w-auto max-w-[180px] object-contain"
              />
            </div>
          )}
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-strong">{productName}</h1>
              <p className="mt-1 text-sm text-muted">
                {anchorLabel
                  ? t("login.subtitle", { anchor: anchorLabel })
                  : t("login.subtitleNoAnchor")}
              </p>
              {tagline && <p className="mt-1 text-xs italic text-muted">{tagline}</p>}
            </div>
            <LanguagePicker />
          </div>

          {error && (
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {stage.kind === "credentials" && (
            <form onSubmit={handleCredentials} className="space-y-4">
              <Field
                label={t("login.fields.email")}
                type="email"
                autoComplete="username"
                value={email}
                onChange={setEmail}
                required
              />
              <Field
                label={t("login.fields.password")}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                required
              />
              <div className="flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-line-strong accent-brand-primary"
                  />
                  <span className="text-muted">
                    {t("login.rememberEmail", { defaultValue: "Remember email" })}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setStage({ kind: "forgot_email" });
                  }}
                  className="text-xs font-medium text-foreground hover:text-strong"
                >
                  {t("login.forgotPassword")}
                </button>
              </div>
              <SubmitButton pending={pending}>{t("common.signIn")}</SubmitButton>
            </form>
          )}

          {stage.kind === "forgot_email" && (
            <form onSubmit={handleForgotEmail} className="space-y-4">
              <p className="text-xs text-foreground">{t("login.forgot.body")}</p>
              <Field
                label={t("login.fields.email")}
                type="email"
                autoComplete="username"
                value={email}
                onChange={setEmail}
                required
              />
              <SubmitButton pending={pending}>{t("login.forgot.sendCode")}</SubmitButton>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setStage({ kind: "credentials" });
                }}
                className="block w-full text-center text-xs font-medium text-foreground hover:text-strong"
              >
                {t("login.forgot.back")}
              </button>
            </form>
          )}

          {stage.kind === "forgot_code" && (
            <form onSubmit={handleForgotCode} className="space-y-4">
              <p className="text-xs text-foreground">
                {t("login.forgot.codeBody", { email: stage.emailHint })}
              </p>
              <Field
                label={t("login.fields.totpCode")}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={setCode}
                required
              />
              <Field
                label={t("login.fields.newPassword")}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={setNewPassword}
                required
              />
              <SubmitButton pending={pending}>{t("login.forgot.confirm")}</SubmitButton>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setStage({ kind: "credentials" });
                }}
                className="block w-full text-center text-xs font-medium text-foreground hover:text-strong"
              >
                {t("login.forgot.back")}
              </button>
            </form>
          )}

          {stage.kind === "totp" && (
            <form onSubmit={handleTotp} className="space-y-4">
              <Field
                label={t("login.fields.totpCode")}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={setCode}
                required
              />
              <SubmitButton pending={pending}>{t("common.verify")}</SubmitButton>
            </form>
          )}

          {stage.kind === "new_password" && (
            <form onSubmit={handleNewPassword} className="space-y-4">
              <Field
                label={t("login.fields.newPassword")}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={setNewPassword}
                required
              />
              <SubmitButton pending={pending}>{t("common.setPassword")}</SubmitButton>
            </form>
          )}

          {stage.kind === "mfa_setup" && (
            <form onSubmit={handleMfaSetup} className="space-y-4">
              <p className="text-xs text-foreground">{t("login.mfaSetup.body")}</p>
              <div className="flex justify-center rounded-lg border border-line bg-white p-4">
                <QRCodeSVG
                  value={stage.qrUri}
                  size={160}
                  marginSize={1}
                  aria-label={t("login.mfaSetup.qrAlt")}
                />
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted">
                  {t("login.mfaSetup.cantScan")}
                </summary>
                <code className="mt-2 block break-all rounded border border-line bg-surface-strong p-2 font-mono text-[11px]">
                  {stage.secretCode}
                </code>
              </details>
              <Field
                label={t("login.mfaSetup.codeLabel")}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={setCode}
                required
              />
              <SubmitButton pending={pending}>{t("login.mfaSetup.verify")}</SubmitButton>
            </form>
          )}
        </div>
        <PoweredBy brand={merged} variant="inline" className="mt-4" />
      </div>
    </>
  );
}

interface FieldProps {
  readonly label: string;
  readonly type: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly required?: boolean;
  readonly autoComplete?: string;
  readonly inputMode?: "numeric" | "text";
  readonly autoFocus?: boolean;
}

function Field({
  label,
  type,
  value,
  onChange,
  required,
  autoComplete,
  inputMode,
  autoFocus,
}: FieldProps) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const resolvedType = isPassword && showPw ? "text" : type;
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="relative mt-1">
        <input
          type={resolvedType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoFocus={autoFocus}
          {...(autoComplete ? { autoComplete } : {})}
          {...(inputMode ? { inputMode } : {})}
          className={`block w-full rounded-md border border-line-strong py-2 text-sm shadow-sm focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-primary ${isPassword ? "pr-9 pl-3" : "px-3"}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPw((s) => !s)}
            className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
            aria-label={showPw ? "Hide password" : "Show password"}
          >
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
    </label>
  );
}

function SubmitButton({ children, pending }: { children: React.ReactNode; pending: boolean }) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-fg shadow-sm transition hover:opacity-90 disabled:opacity-60"
    >
      {pending && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

function FullScreenSpinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-strong">
      <div className="flex items-center gap-2 text-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
}

function FullScreenMessage({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "warn" | "error";
}) {
  const cls =
    tone === "error"
      ? "border-red-200 bg-red-50 text-red-800"
      : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-strong px-4">
      <div className={`max-w-md rounded-md border p-6 ${cls}`}>{children}</div>
    </div>
  );
}

function pickStr(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  return typeof v === "string" && v.trim().length > 0 ? v : null;
}

function pickHttpsUrl(obj: Record<string, unknown>, key: string): string | null {
  const v = pickStr(obj, key);
  return v && /^https:\/\//i.test(v.trim()) ? v.trim() : null;
}

function humaniseLoginError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  // Cognito errors come pre-translated by the SDK to English. We
  // surface them as-is rather than re-translating because per-error
  // localisation would need a giant Cognito error → key mapping
  // table; the common errors are recognisable to most ops staff.
  return "Sign-in failed. Try again.";
}
